/**
 * Concurrency-safe reconcile for the content editor.
 *
 * Given the payload as it was loaded (`baseline`), the locally edited payload
 * (`draft`), and the current server payload (`server`, freshly fetched at save
 * time), produce a merged payload that applies ONLY the changes this session
 * made — on top of whatever another admin may have saved meanwhile.
 *
 * This means two admins editing different fields both keep their work. Only when
 * both edit the exact same leaf does last-writer-win apply.
 */

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function deepEqual(a, b) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * @param {*} server current server value at this node
 * @param {*} baseline value at load time
 * @param {*} draft locally edited value
 */
export function reconcile(server, baseline, draft) {
  // Leaf (or array) on either side → the session's value wins only if it
  // actually changed vs. what was loaded; otherwise keep the server's value.
  if (!isPlainObject(draft) || !isPlainObject(baseline)) {
    return deepEqual(draft, baseline) ? server : draft;
  }

  const out = isPlainObject(server) ? { ...server } : {};
  const keys = new Set([...Object.keys(baseline), ...Object.keys(draft)]);

  for (const key of keys) {
    const inDraft = Object.prototype.hasOwnProperty.call(draft, key);
    const inBaseline = Object.prototype.hasOwnProperty.call(baseline, key);

    if (inDraft && inBaseline) {
      out[key] = reconcile(
        Object.prototype.hasOwnProperty.call(out, key) ? out[key] : baseline[key],
        baseline[key],
        draft[key]
      );
    } else if (inDraft && !inBaseline) {
      // Added this session.
      out[key] = draft[key];
    } else if (!inDraft && inBaseline) {
      // Removed this session (e.g. "Resetează") → drop from server result too,
      // but only if the server hasn't changed it away from the loaded value.
      if (deepEqual(out[key], baseline[key])) {
        delete out[key];
      }
    }
  }

  return out;
}
